/**
 * Site-wide <head> security tags, rendered by src/routes/__root.tsx head()
 * into every server-rendered page (they replaced the old static index.html).
 * Kept in their own module so tests can check and execute them directly.
 */
import { applyDevCsp } from "./devCsp";
import { isolatedBuiltCsp } from "./isolatedBuiltCsp";

// Content-Security-Policy, carried over from the pre-upgrade index.html.
// `frame-ancestors`, `report-uri` and `sandbox` are ignored when CSP arrives
// as a <meta> tag (browser spec), so they are deliberately absent.
// worker-src allows blob: because Sentry replay builds compression workers from blob URLs.
export const CONTENT_SECURITY_POLICY = [
  "default-src 'self'",
  "script-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://fonts.gstatic.com https://api.mapbox.com https://events.mapbox.com https://cdn.gpteng.co https://cdn.tailwindcss.com https://www.googletagmanager.com https://challenges.cloudflare.com",
  "worker-src 'self' blob:",
  "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://api.mapbox.com",
  "font-src 'self' https://fonts.gstatic.com data:",
  "img-src 'self' data: blob: https:",
  "connect-src 'self' https://track.akiva.com https://*.supabase.co wss://*.supabase.co https://api.mapbox.com https://events.mapbox.com https://ai.gateway.lovable.dev https://*.gpteng.co https://challenges.cloudflare.com https://*.ingest.sentry.io https://*.ingest.us.sentry.io",
  "frame-src 'self' https://challenges.cloudflare.com",
  "object-src 'none'",
  "base-uri 'self'",
  "form-action 'self'",
  "upgrade-insecure-requests",
].join("; ");

// Clickjacking stopgap (carried over from index.html). Not equivalent to
// X-Frame-Options / frame-ancestors, which this hosting cannot send yet.
// Same-origin framing and the Lovable builder preview (checked by exact
// origin or subdomain of lovable.dev / lovable.app, never a substring) are
// allowed; any other framing parent is busted before the page paints.
export const FRAME_GUARD_SCRIPT = `(function () {
  if (window.top === window.self) return;
  try {
    void window.top.location.origin;
    return;
  } catch (e) {
    var ALLOWED = ['https://lovable.dev', 'https://lovable.app'];
    try {
      var ref = document.referrer;
      if (ref) {
        var o = new URL(ref).origin;
        for (var i = 0; i < ALLOWED.length; i++) {
          if (o === ALLOWED[i] ||
              o.slice(-(ALLOWED[i].length - 'https://'.length + 1)) ===
                '.' + ALLOWED[i].slice('https://'.length)) {
            return;
          }
        }
      }
    } catch (_) {}
    try {
      document.documentElement.style.display = 'none';
      window.top.location = window.self.location.href;
    } catch (_) {
      document.documentElement.style.display = 'none';
    }
  }
})();`;

/**
 * The CSP the page ships. On the dev server only, connect-src also allows the
 * configured Supabase origin, so local dev and E2E against a local Supabase
 * (http://127.0.0.1:54321) are not blocked. Production builds never see it.
 */
export function contentSecurityPolicy(): string {
  const policy = import.meta.env.DEV
    ? applyDevCsp(`${CONTENT_SECURITY_POLICY};`, import.meta.env.VITE_SUPABASE_URL).replace(/;$/, "")
    : CONTENT_SECURITY_POLICY;
  return isolatedBuiltCsp(policy, import.meta.env.MODE, import.meta.env.VITE_SUPABASE_URL);
}

/**
 * The site-wide head tags as static HTML, in the order the server renders
 * them (head scripts run before the app bundle, which loads at end of body).
 */
export function staticHeadHtml(csp: string = CONTENT_SECURITY_POLICY): string {
  return [
    `<meta http-equiv="Content-Security-Policy" content="${csp}" />`,
    `<script>${FRAME_GUARD_SCRIPT}</script>`,
  ].join("\n");
}
